import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
	setVodQuery,
	fetchVod,
	deleteVodSeries,
	deleteVodSeason,
	deleteVod,
	setVodSubtype,
	setVodStatus,
	fetchCategories,
	createFiltersSuggestions,
	fetchProviders,
	fetchInstitutionsList,
	dictionaryParentalControlRating,
	dictionaryPaymentModels,
	dictionaryVODSubstatus,
	setFilterUrlQuery,
	setFilterFormValues
} from 'store/actions';
import { useLocation } from 'react-router-dom';

// Import hooks
import useFilterLogic from 'hooks/useFilterLogic';

// Import columns
import { createColumns } from './vod_columns';

// Import utilities
import { Search } from 'components/utilities/search';

// Import hooks
import useAudioLocation from 'hooks/useAudioLocation';

// Import components
import { PanelPageTemplate } from 'components/common/templates';
import FilterSelect from 'components/views/vod/helpers/FilterSelect';
import FilterButton from 'components/common/buttons/FilterButton';

// Import translation
import { useTranslation } from 'react-i18next';

// Import helpers
import { VOD_SUBTYPES, VOD_STATUS } from 'components/helpers/variables';
import { vodCategoriesOptions } from 'components/helpers/genres_helpers/helpers';
import {
	allSubCategories,
	select_products_category
} from 'components/helpers/products_category_sub_category_variables';

const Vod = ({
	error,
	setVodQuery,
	fetchVod,
	deleteVodSeries,
	deleteVodSeason,
	deleteVod,
	resources,
	setVodSubtype,
	setVodStatus,
	fetchCategories,
	createFiltersSuggestions,
	fetchInstitutionsList,
	fetchProviders,
	dictionaryParentalControlRating,
	dictionaryPaymentModels,
	dictionaryVODSubstatus,
	setFilterUrlQuery,
	setFilterFormValues,
	filters
}) => {
	const { t } = useTranslation();
	const { state } = useLocation();

	const { isAudio, locationType } = useAudioLocation();

	useEffect(() => {
		localStorage.removeItem('series');
		localStorage.removeItem('season');
	}, []);

	// if user is redirected from providers list VOD list should
	// be filtered by specific provider
	useEffect(() => {
		if (state && state.provider) {
			setFilterUrlQuery(`columns[6][search][value]=${state.provider}&`);
			setFilterFormValues({ provider: `${state.provider}` });
		}
		// eslint-disable-next-line
	}, []);

	// select proper action to delete vod based on its subtype (vod | series | season | episodes)
	const handleVodDelete = (uuid, { type }) => {
		switch (type) {
			case 'series':
				deleteVodSeries(uuid);
				break;
			case 'season':
				deleteVodSeason(uuid);
				break;

			default:
				deleteVod(uuid);
				break;
		}
	};

	// Actions to fetch data for filters
	const filterActions = {
		fetchCategories,
		fetchProviders,
		fetchInstitutionsList,
		dictionaryParentalControlRating,
		dictionaryPaymentModels,
		dictionaryVODSubstatus
	};

	const sub_category = allSubCategories;
	const category = select_products_category;

	const allFilters = { ...filters, sub_category, category };

	// execute filters logic
	useFilterLogic({
		filterActions,
		categoriesOptions: vodCategoriesOptions,
		createFiltersSuggestions,
		filters: allFilters
	});

	const {
		options: {
			filters: { subtype, status }
		},
		columns
	} = resources;

	const handleCreateColumns = (...rest) => createColumns(...rest, isAudio);

	return (
		<PanelPageTemplate
			error={error}
			type={locationType}
			addLinkText={t('vod:buttons.add_material')}
			resources={resources}
			columns={handleCreateColumns}
			fetchResourcesAction={fetchVod}
			resourcesForFetchAction={{ isMainVodTable: true, isAudio }}
			deleteItemAction={handleVodDelete}
			defaultSorted={[{ id: 'title', desc: false }]}
			isMassChangeTable={true}
			searchColumn="title"
		>
			<div>
				<div className="d-flex align-items-center m-b-sm">
					<FilterSelect
						options={VOD_SUBTYPES(isAudio)}
						label={t('vod:select:label.subtype')}
						action={setVodSubtype}
						defaultValue={subtype}
					/>

					<FilterSelect
						options={VOD_STATUS()}
						label={t('vod:select:label.status')}
						action={setVodStatus}
						defaultValue={status}
					/>
				</div>

				<div className="d-flex justify-content-end">
					<Search
						setQueryValue={setVodQuery}
						value={resources.options.filters.query}
					/>
					<FilterButton tableColumns={columns} />
				</div>
			</div>
		</PanelPageTemplate>
	);
};

Vod.propTypes = {
	error: PropTypes.bool.isRequired,
	setVodQuery: PropTypes.func.isRequired,
	fetchVod: PropTypes.func.isRequired,
	deleteVodSeries: PropTypes.func.isRequired,
	deleteVodSeason: PropTypes.func.isRequired,
	deleteVod: PropTypes.func.isRequired,
	resources: PropTypes.object.isRequired,
	fetchCategories: PropTypes.func.isRequired,
	setVodSubtype: PropTypes.func.isRequired,
	setVodStatus: PropTypes.func.isRequired,
	createFiltersSuggestions: PropTypes.func.isRequired,
	fetchProviders: PropTypes.func.isRequired,
	fetchInstitutionsList: PropTypes.func.isRequired,
	dictionaryParentalControlRating: PropTypes.func.isRequired,
	dictionaryPaymentModels: PropTypes.func.isRequired,
	dictionaryVODSubstatus: PropTypes.func.isRequired,
	setFilterUrlQuery: PropTypes.func.isRequired,
	setFilterFormValues: PropTypes.func.isRequired,
	filters: PropTypes.object.isRequired
};

const mapStateToProps = (state) => ({
	error: state.vod.table.error,
	resources: state.vod.table,
	filters: {
		genres: state.categories.table.data,
		providers: state.providers.table.data,
		institutions: state.dictionary.institutionsList,
		rating: state.dictionary.parentalControlRating,
		payment_models: state.dictionary.paymentModels,
		sub_status: state.dictionary.VODSubStatus
	}
});

export default connect(mapStateToProps, {
	setVodQuery,
	fetchVod,
	deleteVodSeries,
	deleteVodSeason,
	deleteVod,
	setVodSubtype,
	setVodStatus,
	fetchCategories,
	createFiltersSuggestions,
	fetchProviders,
	fetchInstitutionsList,
	dictionaryParentalControlRating,
	dictionaryPaymentModels,
	dictionaryVODSubstatus,
	setFilterUrlQuery,
	setFilterFormValues
})(Vod);
